import "./index.css";
import React from "react";
import PropTypes from "prop-types";
import validator from "validator";
import * as R from "ramda";
import { Dimmer, Loader } from "semantic-ui-react";
import TextField from "material-ui/TextField";
import Select from "material-ui/Select";
import Input, { InputLabel } from "material-ui/Input";
import { FormControl, FormHelperText } from "material-ui/Form";
import mixpanel from "../../services/mixpanel";
import { specialties, countries } from "../../constants";
import ProfilePicture from "../ProfilePicture";
import { getAllOnboardingFields, ONBOARDING_FIELDS, DEFAULT_ONBOARDING_MESSAGE } from "./constants";

const formFieldsNames = getAllOnboardingFields();

const Specialties = ({ speciality, handleSpecialtiesChange, isSpecialityError }) => {
  return (
    <Select
      value={speciality}
      onChange={handleSpecialtiesChange}
      input={<Input id="speciality" />}
      error={isSpecialityError}
      native
      required
    >
      <option value="" disabled />
      {specialties.map((speciality, index) => (
        <option key={index} value={speciality}>
          {speciality}
        </option>
      ))}
    </Select>
  );
};

const Countries = ({ country, handleCountriesChange, isCountryError }) => {
  return (
    <Select
      value={country}
      onChange={handleCountriesChange}
      input={<Input id="country" />}
      error={isCountryError}
      native
      required
    >
      <option value="" disabled />
      {countries.map((country, index) => (
        <option key={index} value={country.countryName}>
          {country.countryName}
        </option>
      ))}
    </Select>
  );
};

export class OnBoardingDimmer extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      country: this.props.user.country || "",
      speciality: this.props.user.speciality || "",
      email: this.props.user.email || "",
      errors: {
        isCountryError: false,
        isSpecialityError: false,
        isEmailError: false,
        generic: false,
      },
      errorMessages: {
        countryMessage: "",
        specialityMessage: "",
        emailMessage: "",
        generic: "",
      },
      displayFields: this.props.displayFields || formFieldsNames,
    };
  }

  shouldDisplayField(fieldName) {
    return this.state.displayFields.indexOf(fieldName) > -1;
  }

  componentDidMount() {
    mixpanel.time_event("handle.onboarding.submit.success");
    mixpanel.time_event("handle.onboarding.submit.error");
  }

  render() {
    if (this.props.isCreatingProfile) {
      return (
        <Dimmer active page>
          <div className="onboarding-container">
            <Loader>Loading</Loader>
          </div>
        </Dimmer>
      );
    }

    return (
      <Dimmer active={this.props.isOpen} page>
        <div className="onboarding-container">
          <section className="inner-wrapper">
            <div className="-profile-img-wrapper">
              <ProfilePicture
                className="-profile-img"
                userId={this.props.user.uid}
                fallbackUrl={this.props.user.picture}
                size="m"
                alt="User Avatar"
              />
            </div>
            <h3 className="-heading">Hi {this.props.user.name}</h3>
            <p className="-paragraph">{this.props.msg || DEFAULT_ONBOARDING_MESSAGE}</p>

            {this.shouldDisplayField(ONBOARDING_FIELDS.EMAIL) ? (
              <FormControl error={this.state.errors.isEmailError} fullWidth>
                <TextField
                  label="Email"
                  type="email"
                  error={this.state.errors.isEmailError}
                  value={this.state.email}
                  onChange={this.handleEmailChange}
                  required
                />
                {this.state.errors.isEmailError && (
                  <FormHelperText> {this.state.errorMessages.emailMessage} </FormHelperText>
                )}
              </FormControl>
            ) : null}
            {this.shouldDisplayField(ONBOARDING_FIELDS.SPECIALITY) ? (
              <FormControl fullWidth>
                <InputLabel htmlFor="speciality">Speciality</InputLabel>
                <Specialties
                  speciality={this.state.speciality}
                  handleSpecialtiesChange={this.handleSpecialtiesChange}
                  isSpecialityError={this.state.errors.isSpecialityError}
                />
              </FormControl>
            ) : null}
            {this.shouldDisplayField(ONBOARDING_FIELDS.COUNTRY) ? (
              <FormControl error={this.state.errors.isCountryError} fullWidth>
                <InputLabel htmlFor="country">Country</InputLabel>
                <Countries
                  country={this.state.country}
                  handleCountriesChange={this.handleCountriesChange}
                  isCountryError={this.state.errors.isCountryError}
                />
                {this.state.errors.isCountryError && (
                  <FormHelperText> {this.state.errorMessages.countryMessage} </FormHelperText>
                )}
              </FormControl>
            ) : null}
            <FormControl error={this.state.errors.generic} fullWidth>
              {this.state.errors.generic && <FormHelperText> {this.state.errorMessages.generic} </FormHelperText>}
            </FormControl>

            <button className="button -rounded -outline" onClick={this.handleSubmit}>
              Submit
            </button>
          </section>
        </div>
      </Dimmer>
    );
  }

  validateAllFields = () => {
    const errors = {
      isCountryError: this.shouldDisplayField("country") && R.isEmpty(this.state.country),
      isSpecialityError: this.shouldDisplayField("speciality") && R.isEmpty(this.state.speciality),
      isEmailError:
        (this.shouldDisplayField("email") && R.isEmpty(this.state.email)) || !validator.isEmail(this.state.email),
    };
    this.setState({
      errors,
    });
    return R.compose(R.isEmpty, R.filter(e => e))(errors);
  };

  handleCountriesChange = e => {
    const { value } = e.target;
    this.setState({
      country: value,
    });
  };

  handleSpecialtiesChange = e => {
    const { value } = e.target;
    this.setState({ speciality: value });
  };

  handleEmailChange = e => {
    const { value } = e.target;
    this.setState({ email: value });
  };

  handleSubmit = e => {
    if (this.validateAllFields()) {
      this.props.onSubmit(R.pick(this.state.displayFields, this.state), this.handleSubmitErrors);
    }
  };

  handleSubmitErrors = error => {
    const errorDataObj = {
      error: error,
      response: error.response || null,
      reason: error.response && error.response.data && error.response.data.error,
    };

    if (errorDataObj.reason === "The email address is improperly formatted.") {
      this.setState(
        R.mergeDeepWith(
          R.concat,
          { errors: { isEmailError: true } },
          {
            errorMessages: { emailMessage: "Please enter valid email address." },
          }
        )
      );
    } else if (errorDataObj.reason.indexOf("expected `email` to be unique") > -1) {
      this.setState(
        R.mergeDeepWith(
          R.concat,
          { errors: { isEmailError: true } },
          {
            errorMessages: { emailMessage: "This email already exists." },
          }
        )
      );
    } else {
      this.setState(
        R.mergeDeepWith(
          R.concat,
          { errors: { generic: true } },
          {
            errorMessages: {
              generic: "Unexpected error occurred, please try again.",
            },
          }
        )
      );
    }
    mixpanel.track("handle.onboarding.submit.error", errorDataObj);
  };
}

OnBoardingDimmer.propTypes = {
  onSubmit: PropTypes.func.isRequired,
};

export default OnBoardingDimmer;



// WEBPACK FOOTER //
// ./src/components/OnBoardingDimmer/index.js